import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { EmailOtpType } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import { PASSWORD_RESET_PATH, passwordResetGate, safeNextPath } from "@/lib/auth/redirect";

/**
 * 인증 메일의 링크가 도착하는 곳. 링크를 로그인 세션으로 바꿔서 `next`로 보낸다.
 * Supabase 이메일 템플릿(Confirm signup / Reset password)이 이 주소를 가리켜야 동작한다:
 *   Confirm signup: {{ .SiteURL }}/auth/confirm?token_hash={{ .TokenHash }}&type=email&next=/
 *   Reset password: {{ .SiteURL }}/auth/confirm?token_hash={{ .TokenHash }}&type=recovery&next=/auth/update-password
 *
 * 커스텀 SMTP 없이 기본 템플릿을 쓰는 동안에는 Supabase가 링크를 검증한 뒤 ?code=...(PKCE)를 붙여 이 주소로
 * 보낸다 — 그 코드는 supabase-js가 페이지를 열 때(detectSessionInUrl) 알아서 세션으로 교환하므로 여기서는
 * 교환이 끝났는지만 확인한다(직접 한 번 더 교환하면 1회용 코드라 실패한다). code 방식은 가입한 브라우저에서
 * 링크를 열어야 동작한다(다른 기기에서 열면 실패 → 운영에서는 템플릿을 token_hash 방식으로 바꾼다).
 *
 * next는 오픈 리다이렉트 방지를 위해 같은 사이트 안의 경로만 허용한다(safeNextPath).
 */
export default function AuthConfirmPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // 인증 토큰은 1회용이라 개발 모드(StrictMode)의 이펙트 이중 실행에서도 한 번만 검증해야 한다.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      const supabase = createClient();
      const next = safeNextPath(params.get("next"));
      const tokenHash = params.get("token_hash");
      const type = params.get("type") as EmailOtpType | null;

      let ok = false;
      if (params.get("code")) {
        const { data } = await supabase.auth.getSession(); // 초기화(코드 교환)가 끝나길 기다린다
        ok = Boolean(data.session);
      } else if (tokenHash && type) {
        const { error } = await supabase.auth.verifyOtp({ type, token_hash: tokenHash });
        ok = !error;
      }

      if (!ok) {
        navigate("/auth/login?error=confirm", { replace: true });
        return;
      }
      if (next === PASSWORD_RESET_PATH) passwordResetGate.open();
      navigate(next, { replace: true });
    })();
  }, [navigate, params]);

  return (
    <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
      확인하는 중이에요...
    </div>
  );
}
